import {
  type InfiniteData,
  type Query,
  type UseInfiniteQueryOptions,
  type UseQueryOptions,
  useInfiniteQuery,
  useQuery,
} from '@tanstack/react-query';

import * as Sentry from '@sentry/react';
import type { ImageView } from '@vizit/api';
import { SENTRY_FEATURE_TAGS, SENTRY_STAGE_TAGS } from '@vizit/constants';
import {
  type ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields,
  type SearchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGetData,
  type SearchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGetError,
  type SearchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGetResponse,
  searchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGet,
} from '../../generated';

export type SearchCollectionImagesQueryVariables =
  SearchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGetData['path'] &
    NonNullable<SearchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGetData['query']>;

export type SearchCollectionImagesQuery = {
  data: SearchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGetResponse;
  total_count: number;
};

/**
 * Extracts the total_count from the Vizit-Total-Count response header
 */
function extractTotalCount(response: Response): number {
  const totalCountHeader = response.headers.get('vizit-total-count');
  return totalCountHeader ? Number.parseInt(totalCountHeader, 10) : 0;
}

export const useSearchCollectionImagesQuery = <
  TData = SearchCollectionImagesQuery,
  TError = SearchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGetError,
>(
  {
    portfolio_id,
    collection_id,
    sort_by,
    parent_id,
    source,
    exclude_source,
    image_ids,
    limit,
    offset,
    sort_direction,
  }: SearchCollectionImagesQueryVariables,
  options?: Omit<UseQueryOptions<SearchCollectionImagesQuery, TError, TData>, 'queryKey' | 'queryFn'>,
) => {
  return useQuery<SearchCollectionImagesQuery, TError, TData>({
    queryKey: [
      'SearchCollectionImages',
      {
        portfolio_id,
        collection_id,
        sort_by,
        parent_id,
        source,
        exclude_source,
        image_ids,
        limit,
        offset,
        sort_direction,
      },
    ],
    queryFn: async () => {
      const { data, response } =
        await searchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGet({
          path: { portfolio_id, collection_id },
          query: {
            sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields | undefined,
            parent_id,
            source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            image_ids: image_ids as string[] | undefined,
            limit,
            offset,
            sort_direction,
          },
          throwOnError: true,
        });
      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    ...options,
  });
};

useSearchCollectionImagesQuery.getKey = (variables: SearchCollectionImagesQueryVariables) => [
  'SearchCollectionImages',
  variables,
];

useSearchCollectionImagesQuery.fetcher = (
  {
    portfolio_id,
    collection_id,
    sort_by,
    parent_id,
    source,
    exclude_source,
    image_ids,
    limit,
    offset,
    sort_direction,
  }: SearchCollectionImagesQueryVariables,
  _headers?: RequestInit['headers'],
) => {
  return async () => {
    const { data, response } =
      await searchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGet({
        path: { portfolio_id, collection_id },
        query: {
          sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields | undefined,
          parent_id,
          source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
          image_ids: image_ids as string[] | undefined,
          limit,
          offset,
          sort_direction,
        },
        throwOnError: true,
      });
    return {
      data: data ?? [],
      total_count: extractTotalCount(response),
    };
  };
};

export type SearchCollectionImagesInfiniteQueryVariables = Omit<SearchCollectionImagesQueryVariables, 'offset'>;

export const useSearchCollectionImagesInfiniteQuery = <
  TData = SearchCollectionImagesQuery,
  TError = SearchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGetError,
>(
  {
    portfolio_id,
    collection_id,
    sort_by,
    parent_id,
    source,
    exclude_source,
    image_ids,
    limit,
    sort_direction,
  }: SearchCollectionImagesInfiniteQueryVariables,
  options?: Omit<
    UseInfiniteQueryOptions<SearchCollectionImagesQuery, TError, TData>,
    'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
  >,
) => {
  return useInfiniteQuery<SearchCollectionImagesQuery, TError, TData>({
    queryKey: [
      'SearchCollectionImagesInfinite',
      {
        portfolio_id,
        collection_id,
        sort_by,
        parent_id,
        source,
        exclude_source,
        image_ids,
        limit,
        sort_direction,
      },
    ],
    queryFn: async ({ pageParam = 0 }) => {
      const { data, response } =
        await searchCollectionImagesV1PortfoliosPortfolioIdAssetsImagesCollectionsCollectionIdImagesGet({
          path: { portfolio_id, collection_id },
          query: {
            sort_by: sort_by as ApiRoutersPortfoliosAssetsImagesCollectionsCollectionsImageViewFields | undefined,
            parent_id,
            source: source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            exclude_source: exclude_source as ('user_uploaded' | 'scraped' | 'generated')[] | undefined,
            image_ids: image_ids as string[] | undefined,
            limit,
            offset: pageParam as number,
            sort_direction,
          },
          throwOnError: true,
        });

      return {
        data: data ?? [],
        total_count: extractTotalCount(response),
      };
    },
    getNextPageParam: (lastPage, allPages) => {
      const pageSize = limit || 12; // Default to 12 if no limit specified
      const currentOffset = (allPages.length - 1) * pageSize;
      const nextOffset = currentOffset + pageSize;

      // Check if we have more data to fetch
      if (lastPage.data.length < pageSize || nextOffset >= lastPage.total_count) {
        return undefined; // No more pages
      }

      return nextOffset;
    },
    getPreviousPageParam: (_, allPages) => {
      // Return undefined when we're on the first/only page (initial load)
      // Return a value when we have multiple pages (subsequent loads)
      return allPages.length <= 1 ? undefined : 0;
    },
    initialPageParam: 0,
    refetchInterval: (
      query: Query<SearchCollectionImagesQuery, TError, InfiniteData<SearchCollectionImagesQuery, unknown>>,
    ) => {
      const maxRetries = typeof options?.retry === 'number' ? options.retry : 100;
      const maxRetriesReached = query.state.dataUpdateCount >= maxRetries;

      // Check all pages for images without scores or URLs
      const allImages = query.state.data?.pages?.flatMap((page: SearchCollectionImagesQuery) => page.data) || [];
      const imagesDoNotHaveVizitScore = allImages.some((image: ImageView) => image.vizit_score === null);
      const imagesDoNotHaveUrl = allImages.some(
        (image: ImageView) => image.url === null || !image.url.includes('/images'),
      );

      if ((imagesDoNotHaveVizitScore || imagesDoNotHaveUrl) && !maxRetriesReached) {
        return 1000 * (query.state.dataUpdateCount + 1);
      }

      if ((imagesDoNotHaveVizitScore || imagesDoNotHaveUrl) && maxRetriesReached) {
        Sentry.withScope((scope) => {
          scope.setFingerprint([
            'GET',
            '/v1/portfolios/{portfolio_id}/assets/images/collections/{collection_id}/images',
            'polling_timeout',
          ]);

          Sentry.captureException(new Error('Max retries reached for search collection images query.'), {
            level: 'warning',
            tags: {
              feature: SENTRY_FEATURE_TAGS.Collection,
              stage: SENTRY_STAGE_TAGS[SENTRY_FEATURE_TAGS.Collection].collectionAssetPolling,
            },
            extra: {
              portfolio_id,
              collection_id,
              retry_count: query.state.dataUpdateCount,
              max_count: maxRetries,
              query_variables: {
                portfolio_id,
                collection_id,
                sort_by,
                parent_id,
                source,
                exclude_source,
                image_ids,
                limit,
                sort_direction,
              },
            },
          });
        });
      }

      return false;
    },
    refetchIntervalInBackground: true,
    ...options,
  });
};
